import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../baseurl";

function getInitials(name = "") {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function dateFromObjectId(id) {
  if (!id || id.length < 8) return "-";
  const timestamp = parseInt(id.substring(0, 8), 16) * 1000;
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toISOString().slice(0, 10);
}

function mapReview(raw) {
  return {
    id: raw._id,
    productName: raw.item?.title ?? "Unknown product",
    name: raw.user?.username ?? "Unknown user",
    email: raw.user?.email ?? "",
    rating: raw.starts ?? 0,
    comment: raw.review ?? "",
    createdAt: dateFromObjectId(raw._id),
  };
}

function Stars({ rating, size = 16 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill={rating >= star ? "currentColor" : "none"}
          className={rating >= star ? "text-teal-500" : "text-gray-300"}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
      ))}
    </div>
  );
}

export default function Adminreviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [productFilter, setProductFilter] = useState("All");
  const [ratingFilter, setRatingFilter] = useState("All");
  const [selectedReview, setSelectedReview] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);


  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);


  const fetchReviews = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${BASE_URL}/reviews`);
      const raw = res.data?.reviews ?? [];
      setReviews(raw.map(mapReview));
    } catch (err) {
      console.error("Failed to fetch reviews:", err);
      setError(
        err.response?.data?.message ||
          "Couldn't load reviews. Is the API running?"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const productOptions = useMemo(
    () => Array.from(new Set(reviews.map((r) => r.productName))).sort(),
    [reviews]
  );

  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${BASE_URL}/reviews/admindelete/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setReviews((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      if (selectedReview && selectedReview.id === deleteTarget.id) {
        setSelectedReview(null);
      }
      setDeleteTarget(null);
    } catch (err) {
      console.error("Failed to delete review:", err);
      setDeleteError(
        err.response?.data?.message || "Couldn't delete the review. Try again."
      );
    } finally {
      setDeleting(false);
    }
  };


  const filteredReviews = reviews.filter((review) => {
    const matchesSearch =
      review.name.toLowerCase().includes(search.toLowerCase()) ||
      review.email.toLowerCase().includes(search.toLowerCase()) ||
      review.comment.toLowerCase().includes(search.toLowerCase());
    const matchesProduct =
      productFilter === "All" || review.productName === productFilter;
    const matchesRating =
      ratingFilter === "All" || review.rating === Number(ratingFilter);
    return matchesSearch && matchesProduct && matchesRating;
  });

  const totalReviews = reviews.length;
  const fiveStarReviews = reviews.filter((r) => r.rating === 5).length;
  const averageRating =
    reviews.length === 0
      ? 0
      : reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;

  const stats = [
    { label: "Total Reviews", value: totalReviews },
    { label: "5-Star Reviews", value: fiveStarReviews },
    { label: "Average Rating", value: `${averageRating.toFixed(1)} / 5` },
  ];

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-gray-800 font-semibold text-2xl">Reviews</h1>
          <p className="text-gray-500 text-sm mt-1">
            View and moderate reviews submitted from product pages
          </p>
        </div>
        <button
          onClick={fetchReviews}
          disabled={loading}
          className="text-sm font-medium text-teal-600 hover:text-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={fetchReviews}
            className="font-medium underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-white border border-gray-200 rounded-lg p-5">
            <p className="text-gray-500 text-xs">{stat.label}</p>
            <p className="text-gray-800 font-semibold text-xl mt-1">
              {loading ? "-" : stat.value}
            </p>
          </div>
        ))}
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
            <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search by name, email, or comment..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-white border border-gray-300 rounded-md pl-9 pr-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500"
          />
        </div>

        <select
          value={productFilter}
          onChange={(e) => setProductFilter(e.target.value)}
          className="bg-white border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-teal-500"
        >
          <option value="All">All Products</option>
          {productOptions.map((product) => (
            <option key={product} value={product}>
              {product}
            </option>
          ))}
        </select>

        <select
          value={ratingFilter}
          onChange={(e) => setRatingFilter(e.target.value)}
          className="bg-white border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-teal-500"
        >
          <option value="All">All Ratings</option>
          {[5, 4, 3, 2, 1].map((star) => (
            <option key={star} value={star}>
              {star} Star{star > 1 ? "s" : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left">
                <th className="px-6 py-4 text-gray-500 font-medium">Reviewer</th>
                <th className="px-6 py-4 text-gray-500 font-medium">Product</th>
                <th className="px-6 py-4 text-gray-500 font-medium">Rating</th>
                <th className="px-6 py-4 text-gray-500 font-medium">Comment</th>
                <th className="px-6 py-4 text-gray-500 font-medium">Date</th>
                <th className="px-6 py-4 text-gray-500 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-gray-400">
                    Loading reviews...
                  </td>
                </tr>
              )}

              {!loading &&
                filteredReviews.map((review) => (
                  <tr
                    key={review.id}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs">
                          {getInitials(review.name)}
                        </div>
                        <div>
                          <p className="text-gray-800 font-medium">{review.name}</p>
                          <p className="text-gray-400 text-xs">{review.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{review.productName}</td>
                    <td className="px-6 py-4">
                      <Stars rating={review.rating} />
                    </td>
                    <td className="px-6 py-4 text-gray-600 max-w-xs">
                      <p className="truncate">{review.comment}</p>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{review.createdAt}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-4">
                        <button
                          onClick={() => setSelectedReview(review)}
                          className="text-teal-600 hover:text-teal-700 font-medium text-sm"
                        >
                          View
                        </button>
                        <button
                          onClick={() => setDeleteTarget(review)}
                          className="text-red-500 hover:text-red-600 font-medium text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

              {!loading && !error && filteredReviews.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-gray-400">
                    No reviews match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedReview && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setSelectedReview(null)}
          />

          <div className="fixed top-0 right-0 h-full w-full max-w-[480px] bg-white z-50 flex flex-col shadow-xl">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs">
                  {getInitials(selectedReview.name)}
                </div>
                <div>
                  <h2 className="text-gray-800 font-semibold text-lg">{selectedReview.name}</h2>
                  <p className="text-gray-400 text-xs mt-0.5">{selectedReview.email}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedReview(null)}
                className="text-gray-400 hover:text-gray-700"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="mb-6">
                <label className="block text-gray-500 text-xs font-medium mb-2">Product</label>
                <p className="text-gray-800 text-sm">{selectedReview.productName}</p>
              </div>

              <div className="mb-6">
                <label className="block text-gray-500 text-xs font-medium mb-2">Rating</label>
                <Stars rating={selectedReview.rating} size={20} />
              </div>

              <div className="mb-6">
                <label className="block text-gray-500 text-xs font-medium mb-2">Comment</label>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">
                    {selectedReview.comment}
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-500 text-xs font-medium mb-2">Submitted</label>
                <p className="text-gray-600 text-sm">{selectedReview.createdAt}</p>
              </div>
            </div>
          </div>
        </>
      )}

      {deleteTarget && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setDeleteTarget(null)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-sm p-6">
              <h3 className="text-gray-800 font-semibold text-lg">Delete Review</h3>
              <p className="text-gray-500 text-sm mt-2">
                Are you sure you want to delete this review from "{deleteTarget.name}"? This can't be undone.
              </p>

             {deleteError && (
                <p className="text-red-500 text-sm mt-4">{deleteError}</p>
              )}

              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={confirmDelete}
                  disabled={deleting}
                  className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-md py-2.5 transition-colors"
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
                <button
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                  className="flex-1 border border-gray-300 text-gray-600 font-medium text-sm rounded-md py-2.5 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}